import { createContext, useContext, useEffect, useState } from "react";
import api, { apiErr, setCurrency } from "@/lib/api";
import { hasPin, pinScope, purgeLegacyPin } from "@/lib/pin";

const AuthContext = createContext(null);

// Ne persiste qu'un sous-ensemble non sensible du profil (pour le mode hors-ligne) — pas l'objet complet.
const SAFE_USER_FIELDS = ["id", "name", "email", "role", "is_owner", "tenant_id", "activity_ids", "allowed_modules", "location_ids", "can_void_sales", "can_adjust_stock", "must_change_password", "status", "mfa"];
const slimUser = (u) => {
  if (!u || typeof u !== "object") return u;
  const o = {};
  for (const k of SAFE_USER_FIELDS) if (u[k] !== undefined) o[k] = u[k];
  return o;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = auth
  const [locked, setLocked] = useState(false); // offline-restored session awaiting PIN

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => { purgeLegacyPin(); setUser(r.data); try { localStorage.setItem("biashara_user", JSON.stringify(slimUser(r.data))); sessionStorage.setItem("biashara_unlocked", "1"); } catch (e) {} })
      .catch((e) => {
        if (e && e.response) { setUser(false); localStorage.removeItem("biashara_user"); }
        else { // network error (offline): keep the known user; lock behind THIS user's PIN if one is set
          try {
            const c = localStorage.getItem("biashara_user");
            if (c) { const cu = JSON.parse(c); setUser(cu); if (hasPin(pinScope(cu)) && !sessionStorage.getItem("biashara_unlocked")) setLocked(true); }
            else setUser(false);
          } catch { setUser(false); }
        }
      });
  }, []);

  // Retour réseau : le serveur (cookie de session) fait autorité sur l'identité.
  // Si le serveur confirme un compte différent de la session restaurée, on réinitialise le verrou.
  useEffect(() => {
    const onOnline = async () => {
      try {
        const { data } = await api.get("/auth/me");
        purgeLegacyPin();
        try { localStorage.setItem("biashara_user", JSON.stringify(slimUser(data))); } catch (e) {}
        setUser((prev) => {
          if (prev && prev.id && prev.id !== data.id) {
            try { sessionStorage.removeItem("biashara_unlocked"); } catch (e) {}
            setLocked(hasPin(pinScope(data)));
          }
          return data;
        });
      } catch (e) {}
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, []);

  const unlock = () => { try { sessionStorage.setItem("biashara_unlocked", "1"); } catch (e) {} setLocked(false); };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setCurrency("XOF");
    purgeLegacyPin();
    setUser(data);
    try { localStorage.setItem("biashara_user", JSON.stringify(slimUser(data))); sessionStorage.setItem("biashara_unlocked", "1"); } catch (e) {}
    return data;
  };
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setCurrency("XOF");
    setUser(data);
    try { localStorage.setItem("biashara_user", JSON.stringify(slimUser(data))); sessionStorage.setItem("biashara_unlocked", "1"); } catch (e) {}
    return data;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) {}
    localStorage.removeItem("biashara_user");
    sessionStorage.removeItem("biashara_unlocked");
    // Purge des caches hors-ligne isolés (activités/réglages) du compte qui se déconnecte.
    try { Object.keys(localStorage).forEach((k) => { if (k.startsWith("biashara.activities.") || k.startsWith("biashara.settings.")) localStorage.removeItem(k); }); } catch (e) {}
    setCurrency("XOF");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, apiErr, locked, unlock }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
